import React, { useState, useEffect } from 'react';
import { get, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Attendance() {
  const { user, hasPermission } = useAuth();
  const isAdmin = ['super_admin','admin'].includes(user?.role);
  const canRequest =
    isAdmin ||
    [
      'production_team_lead'
    ].includes(user?.role) ||
    hasPermission('overtime:request');

  const hasFullAttendanceAccess =
    isAdmin ||
    user?.role === 'hr_officer' ||
    hasPermission('attendance:write');

  const requestOnly =
    canRequest &&
    !hasFullAttendanceAccess;

  const canOpenAttendance =
    hasFullAttendanceAccess ||
    requestOnly;
  const [records, setRecords] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // T500 CONTROL UI
  const [t500Control, setT500Control] =
    useState({
      enabled: false,
      active_now: false,
      effective_from: null,
      reason: null,
      exception_periods: []
    });

  const [
    t500ControlLoading,
    setT500ControlLoading
  ] = useState(true);

  const [
    t500ControlSaving,
    setT500ControlSaving
  ] = useState(false);

  const [activeTab, setActiveTab] =
    useState(
      requestOnly
        ? 'overtime'
        : 'records'
    );
  const [kioskSessions, setKioskSessions] = useState([]);
  const [reconciliation, setReconciliation] = useState([]);
  const [reconSummary, setReconSummary] = useState({
    total: 0,
    requires_review: 0,
    matched: 0,
    pending: 0,
    investigated: 0
  });
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState('');
  const [reconReviewOnly, setReconReviewOnly] = useState(false);
  const [kioskDate, setKioskDate] = useState(new Date().toISOString().split('T')[0]);
  const [kioskLoading, setKioskLoading] = useState(false);
  const [reconFrom, setReconFrom] = useState(
    new Date().toISOString().slice(0, 8) + '01'
  );

  useEffect(() => {
    if (activeTab === 'kiosk') {
      setKioskLoading(true);
      get('/kiosk-sessions/today?date='+kioskDate)
        .then(d => setKioskSessions(d.sessions||[]))
        .catch(()=>{})
        .finally(() => setKioskLoading(false));
    }
  }, [activeTab, kioskDate]);
  const [reconTo, setReconTo] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [filterUser, setFilterUser] = useState('');
  const [breaks, setBreaks] = useState([]);
  const [weekendRequests, setWeekendRequests] = useState([]);
  const [settings, setSettings] = useState({
    break_allowance_minutes:30,
    break_penalty_per_minute:0,
    weekend_pay_rate:0,
    overtime_pay_per_hour:0,
    staff_overtime_rate_per_minute:0,
    manager_overtime_rate_per_minute:0,
    staff_night_rate_per_minute:0,
    manager_night_rate_per_minute:0,
    staff_weekend_rate_per_minute:0,
    manager_weekend_rate_per_minute:0
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [weekendWorkRequests, setWeekendWorkRequests] = useState([]);
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [shiftRequests, setShiftRequests] = useState([]);
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [submittingExtraTime, setSubmittingExtraTime] = useState(false);
  const [shiftForm, setShiftForm] = useState({ user_id:'', date:'', shift_type:'night', reason:'', production_support:false, production_support_note:'' });
  const [showWeekendForm, setShowWeekendForm] = useState(false);
  const [showOvertimeForm, setShowOvertimeForm] = useState(false);
  const [weekendForm, setWeekendForm] = useState({ date:'', reason:'', staff_ids:[], production_support:false, production_support_note:'' });
  const [overtimeForm, setOvertimeForm] = useState({ date:'', user_id:'', expected_hours:'', reason:'', production_support:false, production_support_note:'' });
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectType, setRejectType] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [penaltyExceptions, setPenaltyExceptions] = useState([]);
  const [showExceptionForm, setShowExceptionForm] = useState(false);
  const [exceptionForm, setExceptionForm] = useState({ user_id:'', date:'', exception_type:'Lateness', reason:'' });
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [users, setUsers] = useState([]);
  const [
    eligibleOvertimeStaff,
    setEligibleOvertimeStaff
  ] = useState([]);
  const [
    eligibleStaffError,
    setEligibleStaffError
  ] = useState('');

  const loadT500Control = () => {
    setT500ControlLoading(true);

    return get('/attendance/t500-control')
      .then(response => {
        setT500Control(
          response.data || {
            enabled: false,
            active_now: false,
            effective_from: null,
            reason: null,
            exception_periods: []
          }
        );
      })
      .catch(error => {
        setT500Control({
          enabled: false,
          active_now: false,
          effective_from: null,
          reason:
            error.message ||
            'T500 control status could not be loaded',
          exception_periods: []
        });
      })
      .finally(() =>
        setT500ControlLoading(false)
      );
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/attendance'),
      get('/attendance/uploads')
    ]).then(([recs, ups]) => {
      setRecords(recs.data||[]);
      setUploads(ups.data||[]);
    }).finally(() => setLoading(false));
  };

  const loadEligibleOvertimeStaff = () => {
    if (!canRequest) {
      setEligibleOvertimeStaff([]);
      return Promise.resolve();
    }

    setEligibleStaffError('');

    return get(
      '/kiosk/overtime-eligible-staff'
    )
      .then(data => {
        setEligibleOvertimeStaff(
          data.data || []
        );
      })
      .catch(error => {
        setEligibleOvertimeStaff([]);
        setEligibleStaffError(
          error.message ||
          'Eligible staff could not be loaded'
        );
      });
  };

  useEffect(() => {
    if (!canOpenAttendance) {
      setLoading(false);
      return;
    }

    get('/kiosk/staff')
      .then(d =>
        setUsers(d.data || [])
      )
      .catch(() =>
        setUsers([])
      );

    loadEligibleOvertimeStaff();

    if (requestOnly) {
      setLoading(false);

      get('/kiosk/weekend-work-requests')
        .then(d =>
          setWeekendWorkRequests(
            d.data || []
          )
        )
        .catch(() => {});

      get('/kiosk/overtime-requests')
        .then(d =>
          setOvertimeRequests(
            d.data || []
          )
        )
        .catch(() => {});

      get('/kiosk-sessions/shift-requests')
        .then(d =>
          setShiftRequests(
            d.data || []
          )
        )
        .catch(() => {});

      return;
    }

    load();
    loadT500Control();

    get('/kiosk/breaks')
      .then(d =>
        setBreaks(d.data || [])
      )
      .catch(() => {});

    // Kiosk sessions load only when their tab is selected.
    get('/kiosk/settings')
      .then(d => {
        if (d) setSettings(d.data || d);
      })
      .catch(() => {});

    get('/kiosk/weekend-requests')
      .then(d =>
        setWeekendRequests(d.data || [])
      )
      .catch(() => {});

    get('/kiosk/weekend-work-requests')
      .then(d =>
        setWeekendWorkRequests(
          d.data || []
        )
      )
      .catch(() => {});

    get('/kiosk/overtime-requests')
      .then(d =>
        setOvertimeRequests(
          d.data || []
        )
      )
      .catch(() => {});

    get('/kiosk-sessions/shift-requests')
      .then(d =>
        setShiftRequests(d.data || [])
      )
      .catch(() => {});

    get('/kiosk/penalty-exceptions')
      .then(d =>
        setPenaltyExceptions(d.data || [])
      )
      .catch(() => {});
  }, []);

  const updateT500Control = async () => {
    const reactivating =
      !t500Control.active_now;

    const defaultDate =
      new Date()
        .toISOString()
        .split('T')[0];

    const effectiveFrom =
      window.prompt(
        reactivating
          ? 'Enter the date T500 reconciliation should resume:'
          : 'Enter the date T500 suspension begins:',
        defaultDate
      );

    if (!effectiveFrom) return;

    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(
        effectiveFrom
      )
    ) {
      alert('Enter a valid date.');
      return;
    }

    const reason =
      window.prompt(
        reactivating
          ? 'State why T500 is ready for use again:'
          : 'State why T500 is being suspended:'
      );

    if (
      !reason ||
      reason.trim().length < 10
    ) {
      alert(
        'Enter a clear reason of at least 10 characters.'
      );
      return;
    }

    if (
      !window.confirm(
        reactivating
          ? `Reactivate T500 reconciliation from ${effectiveFrom}?`
          : `Suspend T500 reconciliation from ${effectiveFrom}?`
      )
    ) {
      return;
    }

    setT500ControlSaving(true);

    try {
      const response = await put(
        '/attendance/t500-control',
        {
          enabled: reactivating,
          effective_from: effectiveFrom,
          reason: reason.trim()
        }
      );

      await loadT500Control();

      alert(
        response.message ||
        (
          reactivating
            ? 'T500 reconciliation reactivated'
            : 'T500 reconciliation suspended'
        )
      );
    } catch (error) {
      alert(
        error.message ||
        'T500 control could not be updated'
      );
    } finally {
      setT500ControlSaving(false);
    }
  };


  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/attendance/upload', {
        method: 'POST',
        headers: { Authorization: 'Bearer '+token },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUploadResult(data);
      load();
    } catch (err) { setUploadResult({ error: err.message }); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const loadReconciliation = async () => {
    if (!reconFrom || !reconTo) {
      setReconError('Select both dates.');
      return;
    }

    if (reconFrom > reconTo) {
      setReconError('The start date cannot be after the end date.');
      return;
    }

    setReconLoading(true);
    setReconError('');

    try {
      let url =
        '/attendance/reconciliation?from=' +
        encodeURIComponent(reconFrom) +
        '&to=' +
        encodeURIComponent(reconTo);

      if (reconReviewOnly) {
        url += '&requires_review=true';
      }

      const response = await get(url);

      setReconciliation(response.data || []);
      setReconSummary(response.summary || {
        total: 0,
        requires_review: 0,
        matched: 0,
        pending: 0,
        investigated: 0
      });
    } catch (err) {
      setReconciliation([]);
      setReconSummary({
        total: 0,
        requires_review: 0,
        matched: 0,
        pending: 0,
        investigated: 0
      });
      setReconError(
        err.message || 'Could not load attendance reconciliation.'
      );
    } finally {
      setReconLoading(false);
    }
  };

  const investigateReconciliation = async record => {
    const notes = window.prompt(
      'Enter the investigation finding and decision:'
    );

    if (!notes || !notes.trim()) return;

    try {
      const response = await fetch(
        '/api/attendance/reconciliation/' +
          record.id +
          '/investigate',
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization':
              'Bearer ' + localStorage.getItem('bb_token')
          },
          body: JSON.stringify({ notes: notes.trim() })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || 'Could not save investigation.'
        );
      }

      await loadReconciliation();
    } catch (err) {
      setReconError(
        err.message || 'Could not save investigation.'
      );
    }
  };

  const reconciliationReason = value => {
    const labels = {
      absent_from_both_systems:
        'Absent from both T500 and kiosk',
      missing_kiosk_session:
        'T500 record exists but kiosk session is missing',
      missing_t500_record:
        'Kiosk session exists but T500 record is missing',
      sign_in_mismatch:
        'Sign-in times differ beyond tolerance',
      sign_out_mismatch:
        'Sign-out times differ beyond tolerance',
      sign_in_and_out_mismatch:
        'Sign-in and sign-out times differ beyond tolerance',
      partial_day_pending:
        'Day was incomplete when this partial file was uploaded'
    };

    return labels[value] ||
      String(value || 'No discrepancy')
        .replace(/_/g, ' ');
  };

  const reconciliationTime = value =>
    value ? String(value).slice(0, 5) : 'None';

  const saveSettings = async () => {
    const rateFields = [
      'staff_overtime_rate_per_minute',
      'manager_overtime_rate_per_minute',
      'staff_night_rate_per_minute',
      'manager_night_rate_per_minute',
      'staff_weekend_rate_per_minute',
      'manager_weekend_rate_per_minute'
    ];

    const invalidField =
      rateFields.find(field => {
        const value =
          Number(settings[field]);

        return (
          !Number.isFinite(value) ||
          value < 0
        );
      });

    if (invalidField) {
      alert(
        'All per-minute rates must be valid non-negative amounts.'
      );
      return;
    }

    setSavingSettings(true);

    try {
      const token =
        localStorage.getItem(
          'bb_token'
        );

      const response = await fetch(
        '/api/kiosk/settings',
        {
          method:'PUT',
          headers:{
            'Content-Type':
              'application/json',
            'Authorization':
              'Bearer ' + token
          },
          body:JSON.stringify({
            ...settings
          })
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
          'Settings could not be saved'
        );
      }

      if (data.data) {
        setSettings(data.data);
      }

      alert(
        'Attendance and extra-time rates saved.'
      );
    } catch (error) {
      alert(error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const deleteUpload = async (upload) => {
    if (!window.confirm('Delete all attendance records for ' + upload.period_start + ' to ' + upload.period_end + '? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('bb_token');
      await fetch('/api/attendance/uploads/'+upload.id, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer '+token }
      });
      load();
    } catch(err) { alert(err.message); }
  };

  const approveWeekend = async (id) => {
    try {
      const token = localStorage.getItem('bb_token');
      await fetch('/api/kiosk/weekend-requests/'+id+'/approve', {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', 'Authorization':'Bearer '+token },
        body: JSON.stringify({ approved_by: user?.id })
      });
      get('/kiosk/weekend-requests').then(d => setWeekendRequests(d.data||[]));
    } catch(err) { alert(err.message); }
  };

  const submitWeekendRequest = async () => {
    if (submittingExtraTime) return;
    if (!weekendForm.production_support || weekendForm.production_support_note.trim().length < 10) {
      alert('Confirm that this work supports production and explain the production work in at least 10 characters.');
      return;
    }
    if (!weekendForm.date || !weekendForm.reason || weekendForm.staff_ids.length === 0) {
      alert('Please fill all fields and select at least one staff member'); return;
    }
    setSubmittingExtraTime(true);
    try {
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/kiosk/weekend-work-requests', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', 'Authorization':'Bearer '+token },
        body: JSON.stringify({ ...weekendForm, requested_by: user?.id, requested_by_name: user?.name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setShowWeekendForm(false);
      setWeekendForm({ date:'', reason:'', staff_ids:[], production_support:false, production_support_note:'' });
      get('/kiosk/weekend-work-requests').then(d => setWeekendWorkRequests(d.data||[]));
      alert('Weekend work request submitted to Managing Director');
    } catch(err) { alert(err.message); } finally { setSubmittingExtraTime(false); }
  };

  const submitShiftRequest = async () => {
    if (submittingExtraTime) return;
    if (!shiftForm.production_support || shiftForm.production_support_note.trim().length < 10) {
      alert('Confirm that this work supports production and explain the production work in at least 10 characters.');
      return;
    }
    if (!shiftForm.user_id || !shiftForm.date || !shiftForm.reason) {
      alert('Please fill all fields'); return;
    }
    setSubmittingExtraTime(true);
    try {
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/kiosk-sessions/shift-requests', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', 'Authorization':'Bearer '+token },
        body: JSON.stringify({ ...shiftForm, requested_by: user?.id, requested_by_name: user?.name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setShowShiftForm(false);
      setShiftForm({ user_id:'', date:'', shift_type:'night', reason:'', production_support:false, production_support_note:'' });
      get('/kiosk-sessions/shift-requests').then(d => setShiftRequests(d.data||[]));
      alert('Night shift request submitted to Managing Director');
    } catch(err) { alert(err.message); } finally { setSubmittingExtraTime(false); }
  };

  const submitOvertimeRequest = async () => {
    if (submittingExtraTime) return;
    if (!overtimeForm.production_support || overtimeForm.production_support_note.trim().length < 10) {
      alert('Confirm that this work supports production and explain the production work in at least 10 characters.');
      return;
    }
    if (!overtimeForm.date || !overtimeForm.user_id || !overtimeForm.expected_hours || !overtimeForm.reason) {
      alert('Please fill all fields'); return;
    }
    setSubmittingExtraTime(true);
    try {
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/kiosk/overtime-requests', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', 'Authorization':'Bearer '+token },
        body: JSON.stringify({ ...overtimeForm, requested_by: user?.id, requested_by_name: user?.name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setShowOvertimeForm(false);
      setOvertimeForm({ date:'', user_id:'', expected_hours:'', reason:'', production_support:false, production_support_note:'' });
      get('/kiosk/overtime-requests').then(d => setOvertimeRequests(d.data||[]));
      alert('Overtime request submitted to Managing Director');
    } catch(err) { alert(err.message); } finally { setSubmittingExtraTime(false); }
  };

  const approveRequest = async (id, type) => {
    if (
      !window.confirm(
        'Approve this request?'
      )
    ) return;

    try {
      const token =
        localStorage.getItem('bb_token');

      const apiBase =
        type.startsWith('kiosk-sessions/')
          ? '/api/' + type
          : '/api/kiosk/' + type;

      const response = await fetch(
        apiBase + '/' + id + '/approve',
        {
          method: 'PUT',
          headers: {
            'Content-Type':
              'application/json',
            'Authorization':
              'Bearer ' + token
          },
          body: JSON.stringify({
            approved_by: user?.id
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
          'The request could not be approved'
        );
      }

      await Promise.all([
        get('/kiosk/weekend-work-requests')
          .then(result =>
            setWeekendWorkRequests(
              result.data || []
            )
          ),
        get('/kiosk/overtime-requests')
          .then(result =>
            setOvertimeRequests(
              result.data || []
            )
          ),
        get('/kiosk-sessions/shift-requests')
          .then(result =>
            setShiftRequests(
              result.data || []
            )
          )
      ]);

      alert(
        data.message ||
        'Request approved successfully'
      );
    } catch (error) {
      alert(
        error.message ||
        'The request could not be approved'
      );
    }
  };

  const rejectRequest = async () => {
    try {
      const token =
        localStorage.getItem('bb_token');

      const apiBase =
        rejectType.startsWith(
          'kiosk-sessions/'
        )
          ? '/api/' + rejectType
          : '/api/kiosk/' + rejectType;

      const response = await fetch(
        apiBase +
          '/' +
          rejectModal +
          '/reject',
        {
          method: 'PUT',
          headers: {
            'Content-Type':
              'application/json',
            'Authorization':
              'Bearer ' + token
          },
          body: JSON.stringify({
            rejected_by: user?.id,
            rejection_reason:
              rejectReason
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
          'The request could not be rejected'
        );
      }

      setRejectModal(null);
      setRejectReason('');

      await Promise.all([
        get('/kiosk/weekend-work-requests')
          .then(result =>
            setWeekendWorkRequests(
              result.data || []
            )
          ),
        get('/kiosk/overtime-requests')
          .then(result =>
            setOvertimeRequests(
              result.data || []
            )
          ),
        get('/kiosk-sessions/shift-requests')
          .then(result =>
            setShiftRequests(
              result.data || []
            )
          )
      ]);

      alert(
        data.message ||
        'Request rejected successfully'
      );
    } catch (error) {
      alert(
        error.message ||
        'The request could not be rejected'
      );
    }
  };

  const submitException = async () => {
    if (!exceptionForm.user_id || !exceptionForm.date || !exceptionForm.reason) {
      alert('Please fill all fields'); return;
    }
    try {
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/kiosk/penalty-exceptions', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', 'Authorization':'Bearer '+token },
        body: JSON.stringify({ ...exceptionForm, requested_by: user?.id, requested_by_name: user?.name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setShowExceptionForm(false);
      setExceptionForm({ user_id:'', date:'', exception_type:'Lateness', reason:'' });
      get('/kiosk/penalty-exceptions').then(d => setPenaltyExceptions(d.data||[]));
      alert('Exception request submitted to Managing Director');
    } catch(err) { alert(err.message); }
  };

  const processPayroll = async () => {
    if (!selectedPeriod) { alert('Select a period to process'); return; }
    if (!window.confirm('This will add all attendance penalties as payroll deductions. Continue?')) return;
    setProcessingPayroll(true);
    try {
      const period = records.find(r => r.period_start === selectedPeriod);
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/kiosk/process-penalties', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', 'Authorization':'Bearer '+token },
        body: JSON.stringify({ period_start: selectedPeriod, period_end: period?.period_end })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      alert(data.message);
      load();
    } catch(err) { alert(err.message); } finally { setProcessingPayroll(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attendance record?')) return;
    try { await del('/attendance/'+id); load(); }
    catch (err) { alert(err.message); }
  };

  const filtered = filterUser
    ? records.filter(r => r.user_id === filterUser || r.staff_name?.toLowerCase().includes(filterUser.toLowerCase()))
    : records;

  // Group by period
  const periods = [...new Set(records.map(r => r.period_start))].sort().reverse();

  const getAttendanceColor = (pct) => {
    if (pct >= 90) return '#2E6B3A';
    if (pct >= 70) return '#8B5E0A';
    return '#8F2E24';
  };

  if (!canOpenAttendance) {
    return (
      <div className="card">
        <h2 className="page-title">
          Attendance access unavailable
        </h2>
        <p className="text-muted text-sm">
          Your role does not include attendance or
          extra-work request access.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">
            {requestOnly
              ? 'Extra Work Requests'
              : 'Attendance'}
          </h2>
          <p
            className="text-muted text-sm"
            style={{marginTop:4}}
          >
            {requestOnly
              ? 'Request overtime, weekend work and night shifts for production support.'
              : 'T500 attendance records imported from USB export'}
          </p>
        </div>
          {isAdmin &&
            t500Control.active_now && (
            <label style={{
              cursor:uploading
                ? 'not-allowed'
                : 'pointer'
            }}>
              <span className="btn btn-primary">
                {uploading
                  ? 'Importing...'
                  : 'Upload T500 File'}
              </span>

              <input
                type="file"
                accept=".xls,.xlsx"
                style={{display:'none'}}
                onChange={handleUpload}
                disabled={uploading}
              />
            </label>
          )}
      </div>

      {/* T500 CONTROL UI */}
        {!requestOnly && !t500ControlLoading && (
          <div style={{
            marginBottom:16,
            padding:'14px 16px',
            borderRadius:10,
            background:
              t500Control.active_now
                ? '#E8F5EB'
                : '#FFF3DC',
            border:
              '1px solid ' +
              (
                t500Control.active_now
                  ? '#B8DEC0'
                  : '#E7C680'
              )
          }}>
            <div style={{
              display:'flex',
              justifyContent:'space-between',
              gap:14,
              alignItems:'center',
              flexWrap:'wrap'
            }}>
              <div>
                <div style={{
                  fontWeight:800,
                  color:
                    t500Control.active_now
                      ? '#2E6B3A'
                      : '#8B5E0A'
                }}>
                  {t500Control.active_now
                    ? 'T500 reconciliation active'
                    : 'T500 reconciliation suspended'}
                </div>

                <div style={{
                  marginTop:4,
                  fontSize:12,
                  color:'#5F554D'
                }}>
                  {t500Control.active_now
                    ? `Effective from ${
                        String(
                          t500Control
                            .effective_from ||
                          ''
                        ).slice(0,10)
                      }`
                    : `Scheduled to resume from ${
                        String(
                          t500Control
                            .effective_from ||
                          ''
                        ).slice(0,10) ||
                        'an unset date'
                      }`}
                </div>

                {t500Control.reason && (
                  <div style={{
                    marginTop:5,
                    fontSize:11,
                    color:'#75685D'
                  }}>
                    {t500Control.reason}
                  </div>
                )}

                {(t500Control
                    .exception_periods || [])
                  .map(period => (
                    <div
                      key={period.id}
                      style={{
                        marginTop:5,
                        fontSize:11,
                        color:'#8F2E24'
                      }}
                    >
                      T500 ignored for{' '}
                      {String(
                        period.period_start
                      ).slice(0,10)}
                      {' to '}
                      {String(
                        period.period_end
                      ).slice(0,10)}.
                      Kiosk attendance is authoritative.
                    </div>
                  ))}
              </div>

              {user?.role ===
                'super_admin' && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={t500ControlSaving}
                  onClick={updateT500Control}
                >
                  {t500ControlSaving
                    ? 'Saving...'
                    : (
                      t500Control.active_now
                        ? 'Suspend T500'
                        : 'Reactivate T500'
                    )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Upload result */}
      {uploadResult && (
        <div style={{marginBottom:16,padding:'14px 18px',borderRadius:8,
          background:uploadResult.error?'#FDEAEA':'#E8F5EB',
          border:'1px solid '+(uploadResult.error?'#F5B8B8':'#B8DEC0')}}>
          {uploadResult.error ? (
            <div style={{color:'#8F2E24',fontSize:13}}>❌ {uploadResult.error}</div>
          ) : (
            <div>
              <div style={{color:'#2E6B3A',fontWeight:600,fontSize:13,marginBottom:6}}>
                ✅ {uploadResult.message}
              </div>
              {uploadResult.unmatched?.length > 0 && (
                <div style={{fontSize:12,color:'#8B5E0A',marginTop:4}}>
                  ⚠ Unmatched staff (no BloansBooks account): {uploadResult.unmatched.join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20,borderBottom:'1px solid var(--border-light)',paddingBottom:12}}>
        {(requestOnly
          ? [
              ['weekend','Weekend Work'],
              ['overtime','Overtime'],
              ['nightshift','Night Shift']
            ]
          : [
              ['records','Attendance Records'],
              ['penalties','Penalties'],
              ['exceptions','Exceptions'],
              ['breaks','Break Records'],
              ['weekend','Weekend Work'],
              ['overtime','Overtime'],
              ['nightshift','Night Shift'],
              ['settings','Settings'],
              ['uploads','Upload History'],
              ['kiosk','Kiosk Sign-ins'],
              ['reconciliation','Reconciliation']
            ]
        ).map(([key,label])=>(
          <button key={key} className={'btn btn-sm '+(activeTab===key?'btn-primary':'btn-ghost')}
            onClick={()=>setActiveTab(key)}>{label}</button>
        ))}
      </div>

      {loading ? <div className="spinner"></div> : (
        <>
          {activeTab==='records' && (
            <div>
              {/* Filter */}
              <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
                <select className="form-control" style={{width:'auto',fontSize:12}}
                  value={filterUser} onChange={e=>setFilterUser(e.target.value)}>
                  <option value="">All Staff</option>
                  {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                {filterUser && (
                  <button className="btn btn-ghost btn-sm" onClick={()=>setFilterUser('')}>Clear</button>
                )}
              </div>

              {records.length === 0 ? (
                <div className="empty-state">
                  <h3>No attendance records yet</h3>
                  <p>Upload a T500 XLS file to import attendance data.</p>
                </div>
              ) : periods.map(period => {
                const periodRecords = filtered.filter(r => r.period_start === period);
                if (periodRecords.length === 0) return null;
                const periodEnd = periodRecords[0]?.period_end;
                return (
                  <div key={period} style={{marginBottom:24}}>
                    <div style={{background:'#3B2A21',padding:'10px 16px',borderRadius:'8px 8px 0 0',
                      display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',
                        color:'#A8893D',fontWeight:600}}>
                        {period} → {periodEnd}
                      </span>
                      <span style={{fontSize:11,color:'rgba(242,239,231,.5)'}}>
                        {periodRecords.length} staff
                      </span>
                    </div>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead>
                        <tr style={{background:'#3B2A21'}}>
                          {['ID','Staff','Required Hrs','Actual Hrs','Late','Early Leave','Days','Absent','Overtime','Match'].map(h=>(
                            <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,
                              color:'#E8C466',textTransform:'uppercase',letterSpacing:'.06em',fontWeight:700}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {periodRecords.map((r,i) => {
                          const attPct = r.days_required > 0 ? (r.days_attended/r.days_required)*100 : 0;
                          const hrsPct = r.required_hours > 0 ? (r.actual_hours/r.required_hours)*100 : 0;
                          return (
                            <tr key={r.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',
                                color:'#8A8A8A',fontSize:11}}>{r.t500_id}</td>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>
                                {r.matched_user_name||r.staff_name}
                              </td>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',
                                fontFamily:'Cormorant Garamond,serif',fontSize:13}}>
                                {Number(r.required_hours).toFixed(1)}h
                              </td>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',
                                fontFamily:'Cormorant Garamond,serif',fontSize:13,
                                color:getAttendanceColor(hrsPct),fontWeight:600}}>
                                {Number(r.actual_hours).toFixed(1)}h
                                <span style={{fontSize:10,marginLeft:4,color:getAttendanceColor(hrsPct)}}>
                                  ({hrsPct.toFixed(0)}%)
                                </span>
                              </td>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                                {r.late_count > 0 ? (
                                  <span style={{color:'#8B5E0A',fontWeight:600}}>
                                    {r.late_count}x ({r.late_minutes}min)
                                  </span>
                                ) : <span style={{color:'#2E6B3A'}}>✓</span>}
                              </td>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                                {r.early_leave_count > 0 ? (
                                  <span style={{color:'#8B5E0A',fontWeight:600}}>
                                    {r.early_leave_count}x ({r.early_leave_minutes}min)
                                  </span>
                                ) : <span style={{color:'#2E6B3A'}}>✓</span>}
                              </td>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',
                                color:getAttendanceColor(attPct),fontWeight:600}}>
                                {r.days_attended}/{r.days_required}
                              </td>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',
                                color:r.days_absent>0?'#8F2E24':'#2E6B3A',fontWeight:r.days_absent>0?600:400}}>
                                {r.days_absent > 0 ? r.days_absent+'d' : '—'}
                              </td>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',
                                color:'#1E4FAD'}}>
                                {r.overtime_hours > 0 ? Number(r.overtime_hours).toFixed(1)+'h' : '—'}
                              </td>
                              <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                                {r.matched_user_name ? (
                                  <span style={{fontSize:9,background:'#E8F5EB',color:'#2E6B3A',
                                    padding:'2px 8px',borderRadius:20,fontWeight:700}}>✓ Matched</span>
                                ) : (
                                  <span style={{fontSize:9,background:'#FDEAEA',color:'#8F2E24',
                                    padding:'2px 8px',borderRadius:20,fontWeight:700}}>⚠ Unmatched</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab==='penalties' && (
            <div>
              <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
                <select className="form-control" style={{width:'auto',fontSize:12}}
                  value={selectedPeriod} onChange={e=>setSelectedPeriod(e.target.value)}>
                  <option value="">Select Period</option>
                  {[...new Set(records.map(r=>r.period_start))].sort().reverse().map(p=>(
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                {user?.role==='super_admin' && selectedPeriod && (
                  <button className="btn btn-primary btn-sm" onClick={processPayroll} disabled={processingPayroll}>
                    {processingPayroll?'Processing...':'⚡ Push Penalties to Payroll'}
                  </button>
                )}
              </div>

              {records.filter(r=>!selectedPeriod||r.period_start===selectedPeriod).length === 0 ? (
                <div className="empty-state"><h3>No records found</h3></div>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'#8F2E24'}}>
                    {['Staff','Period','Late Count','Late Mins','Late Penalty','Early Signout','Early Penalty','Absent Days','Absence Penalty','Exception','Total Penalty'].map(h=>(
                      <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {records.filter(r=>!selectedPeriod||r.period_start===selectedPeriod).map((r,i)=>{
                      const total = parseFloat(r.lateness_penalty||0)+parseFloat(r.early_signout_penalty||0)+parseFloat(r.absence_penalty||0);
                      return (
                        <tr key={r.id} style={{background:r.penalty_exception?'#F0FFF0':total>0?'#FFF8F8':i%2===0?'#fff':'#F9F9F9'}}>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{r.matched_user_name||r.staff_name}</td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',fontSize:10,color:'#8A8A8A'}}>{r.period_start}</td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'center',color:r.late_count>0?'#8B5E0A':'inherit'}}>{r.late_count||0}</td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{r.late_minutes||0}min</td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',color:'#8F2E24',fontWeight:600}}>
                            {parseFloat(r.lateness_penalty||0)>0?'₦'+Number(r.lateness_penalty).toLocaleString():'—'}
                          </td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'center',color:r.early_leave_count>0?'#8B5E0A':'inherit'}}>{r.early_leave_count||0}</td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',color:'#8F2E24'}}>
                            {parseFloat(r.early_signout_penalty||0)>0?'₦'+Number(r.early_signout_penalty).toLocaleString():'—'}
                          </td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'center',color:r.days_absent>0?'#8F2E24':'inherit'}}>{r.days_absent||0}</td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',color:'#8F2E24'}}>
                            {parseFloat(r.absence_penalty||0)>0?'₦'+Number(r.absence_penalty).toLocaleString():'—'}
                          </td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8'}}>
                            {r.penalty_exception ? (
                              <span style={{fontSize:9,background:'#E8F5EB',color:'#2E6B3A',padding:'2px 8px',borderRadius:20,fontWeight:700}}>WAIVED</span>
                            ) : <span style={{color:'#8A8A8A'}}>—</span>}
                          </td>
                          <td style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,
                            fontWeight:700,color:r.penalty_exception?'#2E6B3A':total>0?'#8F2E24':'#2E6B3A'}}>
                            {r.penalty_exception?'₦0 (Waived)':total>0?'₦'+Number(total).toLocaleString():'₦0'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab==='exceptions' && (
            <div>
              <div className="flex-between" style={{marginBottom:16}}>
                <div style={{fontSize:13,color:'#8A8A8A'}}>{penaltyExceptions.length} exception requests</div>
                {['super_admin','admin'].includes(user?.role) && (
                  <button className="btn btn-primary btn-sm" onClick={()=>setShowExceptionForm(true)}>
                    + Request Exception
                  </button>
                )}
              </div>

              {showExceptionForm && (
                <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:12}}>Penalty Exception Request</div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Staff Member *</label>
                      <select className="form-control" value={exceptionForm.user_id}
                        onChange={e=>setExceptionForm({...exceptionForm,user_id:e.target.value})}>
                        <option value="">Select staff</option>
                        {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Date *</label>
                      <input type="date" className="form-control" value={exceptionForm.date}
                        onChange={e=>setExceptionForm({...exceptionForm,date:e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Exception Type *</label>
                    <select className="form-control" value={exceptionForm.exception_type}
                      onChange={e=>setExceptionForm({...exceptionForm,exception_type:e.target.value})}>
                      <option value="Lateness">Lateness</option>
                      <option value="Early Signout">Early Sign-out</option>
                      <option value="Absence">Absence</option>
                      <option value="All Penalties">All Penalties</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason *</label>
                    <textarea className="form-control" rows={3} value={exceptionForm.reason}
                      onChange={e=>setExceptionForm({...exceptionForm,reason:e.target.value})}
                      placeholder="e.g. Key holder was late, premises not accessible until 9:00 AM..." />
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>setShowExceptionForm(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={submitException}>Submit to Managing Director</button>
                  </div>
                </div>
              )}

              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#556B2F'}}>
                  {['Date','Staff','Type','Reason','Requested By','Status',''].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {penaltyExceptions.map((ex,i)=>(
                    <tr key={ex.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{ex.date}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{ex.staff_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#556B2F'}}>{ex.exception_type}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ex.reason}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{ex.requested_by_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'uppercase',
                          background:ex.status==='approved'?'#E8F5EB':ex.status==='rejected'?'#FDEAEA':'#FFF3DC',
                          color:ex.status==='approved'?'#2E6B3A':ex.status==='rejected'?'#8F2E24':'#8B5E0A'}}>
                          {ex.status}
                        </span>
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        {ex.status==='pending' && user?.role==='super_admin' && (
                          <div style={{display:'flex',gap:4}}>
                            <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none',fontSize:10}}
                              onClick={()=>approveRequest(ex.id,'penalty-exceptions')}>✓ Waive</button>
                            <button className="btn btn-danger btn-sm" style={{fontSize:10}}
                              onClick={()=>{setRejectModal(ex.id);setRejectType('penalty-exceptions');setRejectReason('');}}>✗</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab==='breaks' && (
            <div>
              {weekendRequests.length > 0 && (
                <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,padding:'12px 16px',marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:500,color:'#8B5E0A',marginBottom:10}}>
                    ⚠ <strong>{weekendRequests.length}</strong> weekend work record{weekendRequests.length!==1?'s':''} awaiting approval
                  </div>
                  {weekendRequests.map((r,i)=>(
                    <div key={r.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
                      background:'#fff',borderRadius:8,padding:'10px 14px',marginBottom:6}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:600}}>{r.staff_name} — {r.date}</div>
                        <div style={{fontSize:11,color:'#8A8A8A'}}>Weekend work — approval required</div>
                      </div>
                      {user?.role==='super_admin' && (
                        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}}
                          onClick={()=>approveWeekend(r.id)}>✓ Approve</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#556B2F'}}>
                  {['Date','Staff','Break Out','Break In','Duration','Excess','Penalty','Status'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {breaks.map((b,i)=>(
                    <tr key={b.id} style={{background:b.excess_minutes>0?'#FFF8F8':i%2===0?'#fff':'#F9F9F9'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{b.date}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{b.staff_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>
                        {b.break_out ? new Date(b.break_out).toLocaleTimeString('en-NG',{hour:'2-digit',minute:'2-digit'}) : '—'}
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>
                        {b.break_in ? new Date(b.break_in).toLocaleTimeString('en-NG',{hour:'2-digit',minute:'2-digit'}) : '—'}
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,
                        color:b.break_duration>30?'#8F2E24':'#2E6B3A'}}>
                        {b.break_duration||0} min
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24',fontWeight:600}}>
                        {b.excess_minutes>0 ? '+'+b.excess_minutes+' min' : '—'}
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',
                        color:'#8F2E24',fontWeight:600}}>
                        {b.penalty_amount>0 ? '₦'+Number(b.penalty_amount).toLocaleString() : '—'}
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        {b.is_weekend ? (
                          <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                            background:b.weekend_approved?'#E8F5EB':'#FFF3DC',
                            color:b.weekend_approved?'#2E6B3A':'#8B5E0A'}}>
                            {b.weekend_approved?'Weekend ✓':'Weekend ⚠'}
                          </span>
                        ) : (
                          <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                            background:b.break_duration>30?'#FDEAEA':'#E8F5EB',
                            color:b.break_duration>30?'#8F2E24':'#2E6B3A'}}>
                            {b.break_duration>30?'Exceeded':'OK'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab==='weekend' && (
            <div>
              <div style={{background:'#F2EFE7',borderRadius:8,padding:'10px 14px',marginBottom:16,
                display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:13,color:'#3B2A21'}}>
                  <strong>Weekend rate per verified minute:</strong> Staff ₦{Number(settings.staff_weekend_rate_per_minute||0).toLocaleString()} · Managers ₦{Number(settings.manager_weekend_rate_per_minute||0).toLocaleString()}
                </span>
                {canRequest && (
                  <button className="btn btn-primary btn-sm" onClick={()=>{ setShowWeekendForm(true); loadEligibleOvertimeStaff(); }}>
                    + Request Weekend Work
                  </button>
                )}
              </div>

              {showWeekendForm && (
                <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:12}}>Weekend Work Request</div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Date (Saturday/Sunday) *</label>
                      <input type="date" className="form-control" value={weekendForm.date}
                        onChange={e=>setWeekendForm({...weekendForm,date:e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Staff Working *
                    </label>

                    <select
                      className="form-control"
                      value=""
                      onChange={event => {
                        const staffId =
                          event.target.value;

                        if (
                          staffId &&
                          !weekendForm.staff_ids
                            .includes(staffId)
                        ) {
                          setWeekendForm({
                            ...weekendForm,
                            staff_ids: [
                              ...weekendForm.staff_ids,
                              staffId
                            ]
                          });
                        }
                      }}
                    >
                      <option value="">
                        Select a staff member
                      </option>
                      {eligibleOvertimeStaff
                        .filter(staffMember =>
                          staffMember.is_active !== false
                        )
                        .map(staffMember => (
                          <option
                            key={staffMember.id}
                            value={staffMember.id}
                            disabled={
                              weekendForm.staff_ids
                                .includes(
                                  staffMember.id
                                )
                            }
                          >
                            {staffMember.name} ({
                              String(
                                staffMember.role ||
                                'staff'
                              ).replaceAll('_', ' ')
                            })
                          </option>
                        ))}
                    </select>

                    {eligibleOvertimeStaff.length === 0 && (
                      <div style={{
                        marginTop:8,
                        fontSize:11,
                        color:'#8F2E24'
                      }}>
                        Staff could not be loaded.
                        Refresh the page and try again.
                      </div>
                    )}

                    {weekendForm.staff_ids.length > 0 && (
                      <div style={{
                        display:'flex',
                        flexWrap:'wrap',
                        gap:6,
                        marginTop:10
                      }}>
                        {weekendForm.staff_ids.map(
                          staffId => {
                            const staffMember =
                              eligibleOvertimeStaff.find(
                                item =>
                                  item.id === staffId
                              );

                            return (
                              <span
                                key={staffId}
                                style={{
                                  display:'inline-flex',
                                  alignItems:'center',
                                  gap:6,
                                  padding:'5px 9px',
                                  borderRadius:20,
                                  background:'#556B2F',
                                  color:'#fff',
                                  fontSize:11
                                }}
                              >
                                {staffMember?.name ||
                                  'Selected staff'}
                                <button
                                  type="button"
                                  aria-label="Remove staff"
                                  onClick={() =>
                                    setWeekendForm({
                                      ...weekendForm,
                                      staff_ids:
                                        weekendForm
                                          .staff_ids
                                          .filter(
                                            id =>
                                              id !==
                                              staffId
                                          )
                                    })
                                  }
                                  style={{
                                    border:'none',
                                    background:'transparent',
                                    color:'#fff',
                                    cursor:'pointer',
                                    padding:0,
                                    fontWeight:700
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
<div className="form-group">
                    <label className="form-label">Reason *</label>
                    <textarea className="form-control" rows={2} value={weekendForm.reason}
                      onChange={e=>setWeekendForm({...weekendForm,reason:e.target.value})}
                      placeholder="Why is weekend work needed?" />
                  </div>
                  <ProductionSupportFields value={weekendForm} onChange={setWeekendForm} />
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>setShowWeekendForm(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={submitWeekendRequest} disabled={submittingExtraTime}>Submit Request</button>
                  </div>
                </div>
              )}

              {weekendWorkRequests.length === 0 ? (
                <div className="empty-state"><h3>No weekend work requests yet</h3></div>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'#556B2F'}}>
                    {['Date','Staff','Reason','Requested By','Status',''].map(h=>(
                      <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {weekendWorkRequests.map((r,i)=>(
                      <tr key={r.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                        <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{r.date}</td>
                        <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                          {r.staff_names?.join(', ')||'—'}
                        </td>
                        <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{r.reason}{r.production_support && <div style={{fontSize:11,marginTop:4,color:'#556B2F'}}>Production support: {r.production_support_note || 'Confirmed'}</div>}</td>
                        <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{r.requested_by_name}</td>
                        <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                          <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'uppercase',
                            background:r.status==='approved'?'#E8F5EB':r.status==='rejected'?'#FDEAEA':'#FFF3DC',
                            color:r.status==='approved'?'#2E6B3A':r.status==='rejected'?'#8F2E24':'#8B5E0A'}}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                          {r.status==='pending' && user?.role==='super_admin' && (
                            <div style={{display:'flex',gap:4}}>
                              <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none',fontSize:10}}
                                onClick={()=>approveRequest(r.id,'weekend-work-requests')}>✓ Approve</button>
                              <button className="btn btn-danger btn-sm" style={{fontSize:10}}
                                onClick={()=>{setRejectModal(r.id);setRejectType('weekend-work-requests');setRejectReason('');}}>✗</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab==='overtime' && (
            <div>
              <div className="flex-between" style={{marginBottom:16}}>
                <div style={{fontSize:13,color:'#8A8A8A'}}>{overtimeRequests.length} overtime requests</div>
                {canRequest && (
                  <button className="btn btn-primary btn-sm" onClick={() => {
                      setShowOvertimeForm(true);
                      loadEligibleOvertimeStaff();
                    }}>
                    + Request Overtime
                  </button>
                )}
              </div>

              {showOvertimeForm && (
                <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:12}}>New Overtime Request</div>
                    {eligibleStaffError && (
                      <div
                        className="alert alert-error"
                        style={{marginBottom:10}}
                      >
                        {eligibleStaffError}
                      </div>
                    )}
                    
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Date *</label>
                      <input type="date" className="form-control" value={overtimeForm.date}
                        onChange={e=>setOvertimeForm({...overtimeForm,date:e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Staff Member *</label>
                      <select className="form-control" value={overtimeForm.user_id}
                        onChange={e=>setOvertimeForm({...overtimeForm,user_id:e.target.value})}>
                        <option value="">Select staff</option>
                        {eligibleOvertimeStaff.map(u => (
                            <option
                              key={u.id}
                              value={u.id}
                            >
                              {u.name} ({
                                String(u.role || 'staff').replaceAll('_', ' ')
                              })
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Estimated Hours (planning only) *</label>
                      <input type="number" className="form-control" value={overtimeForm.expected_hours}
                        onChange={e=>setOvertimeForm({...overtimeForm,expected_hours:e.target.value})}
                        placeholder="e.g. 2.5" min="0.01" step="0.01" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason *</label>
                    <textarea className="form-control" rows={2} value={overtimeForm.reason}
                      onChange={e=>setOvertimeForm({...overtimeForm,reason:e.target.value})}
                      placeholder="Why is overtime needed?" />
                  </div>
                  <ProductionSupportFields value={overtimeForm} onChange={setOvertimeForm} />
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>setShowOvertimeForm(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={submitOvertimeRequest} disabled={submittingExtraTime}>Submit Request</button>
                  </div>
                </div>
              )}

              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#556B2F'}}>
                  {['Date','Staff','Estimated Hours','Reason','Requested By','Status',''].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {overtimeRequests.map((r,i)=>(
                    <tr key={r.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.date}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{r.staff_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#1E4FAD'}}>{r.expected_hours}h</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{r.reason}{r.production_support && <div style={{fontSize:11,marginTop:4,color:'#556B2F'}}>Production support: {r.production_support_note || 'Confirmed'}</div>}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{r.requested_by_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'uppercase',
                          background:r.status==='approved'?'#E8F5EB':r.status==='rejected'?'#FDEAEA':'#FFF3DC',
                          color:r.status==='approved'?'#2E6B3A':r.status==='rejected'?'#8F2E24':'#8B5E0A'}}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        {r.status==='pending' && user?.role==='super_admin' && (
                          <div style={{display:'flex',gap:4}}>
                            <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none',fontSize:10}}
                              onClick={()=>approveRequest(r.id,'overtime-requests')}>✓</button>
                            <button className="btn btn-danger btn-sm" style={{fontSize:10}}
                              onClick={()=>{setRejectModal(r.id);setRejectType('overtime-requests');setRejectReason('');}}>✗</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab==='nightshift' && (
            <div>
              <div className="flex-between" style={{marginBottom:16}}>
                <div style={{fontSize:13,color:'#8A8A8A'}}>{shiftRequests.filter(r=>r.shift_type==='night').length} night shift requests</div>
                {canRequest && (
                  <button className="btn btn-primary btn-sm" onClick={() => {
                      setShowShiftForm(true);
                      loadEligibleOvertimeStaff();
                    }}>
                    + Request Night Shift
                  </button>
                )}
              </div>

              {showShiftForm && (
                <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:12}}>Night Shift Request</div>
                    {eligibleStaffError && (
                      <div
                        className="alert alert-error"
                        style={{marginBottom:10}}
                      >
                        {eligibleStaffError}
                      </div>
                    )}
                    
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Staff Member *</label>
                      <select className="form-control" value={shiftForm.user_id}
                        onChange={e=>setShiftForm({...shiftForm,user_id:e.target.value})}>
                        <option value="">Select staff</option>
                        {eligibleOvertimeStaff.map(u => (
                            <option
                              key={u.id}
                              value={u.id}
                            >
                              {u.name} ({
                                String(u.role || 'staff').replaceAll('_', ' ')
                              })
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Date *</label>
                      <input type="date" className="form-control" value={shiftForm.date}
                        onChange={e=>setShiftForm({...shiftForm,date:e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason *</label>
                    <textarea className="form-control" rows={2} value={shiftForm.reason}
                      onChange={e=>setShiftForm({...shiftForm,reason:e.target.value})}
                      placeholder="Why is night shift needed?" />
                  </div>
                  <ProductionSupportFields value={shiftForm} onChange={setShiftForm} />
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>setShowShiftForm(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={submitShiftRequest} disabled={submittingExtraTime}>Submit to Managing Director</button>
                  </div>
                </div>
              )}

              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#3B2A21'}}>
                  {['Date','Staff','Reason','Requested By','Status',''].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {shiftRequests.filter(r=>r.shift_type==='night').map((r,i)=>(
                    <tr key={r.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.date}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{r.staff_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{r.reason}{r.production_support && <div style={{fontSize:11,marginTop:4,color:'#556B2F'}}>Production support: {r.production_support_note || 'Confirmed'}</div>}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{r.requested_by_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'uppercase',
                          background:r.status==='approved'?'#E8F5EB':r.status==='rejected'?'#FDEAEA':'#FFF3DC',
                          color:r.status==='approved'?'#2E6B3A':r.status==='rejected'?'#8F2E24':'#8B5E0A'}}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        {r.status==='pending' && user?.role==='super_admin' && (
                          <div style={{display:'flex',gap:4}}>
                            <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none',fontSize:10}}
                              onClick={()=>approveRequest(r.id,'kiosk-sessions/shift-requests')}>✓</button>
                            <button className="btn btn-danger btn-sm" style={{fontSize:10}}
                              onClick={()=>{setRejectModal(r.id);setRejectType('kiosk-sessions/shift-requests');setRejectReason('');}}>✗</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab==='settings' && user?.role==='super_admin' && (
            <div style={{maxWidth:500}}>
              <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,padding:24}}>
                <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',color:'#3B2A21',marginBottom:20}}>
                  Attendance Policy Settings
                </h3>
                <p style={{fontSize:12,background:'#FFF3DC',padding:12,borderRadius:8}}>October 2026 and later payroll uses the effective policy saved in Payroll → Payroll Settings. Save a payroll policy version there when changing the rates for payroll.</p>
                <div style={{fontSize:11,fontWeight:600,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8,marginTop:4}}>Work Hours</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Work Start Time</label>
                    <input type="time" className="form-control" value={settings.work_start_time||'08:30'}
                      onChange={e=>setSettings({...settings,work_start_time:e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Work End Time</label>
                    <input type="time" className="form-control" value={settings.work_end_time||'18:00'}
                      onChange={e=>setSettings({...settings,work_end_time:e.target.value})} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Grace Period (minutes)</label>
                  <input type="number" className="form-control" value={settings.grace_period_minutes||10}
                    onChange={e=>setSettings({...settings,grace_period_minutes:e.target.value})} />
                  <div className="form-hint">Minutes allowed after start time before lateness penalty applies</div>
                </div>

                <div style={{fontSize:11,fontWeight:600,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8,marginTop:16}}>Lateness Penalties</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Lateness Flat Penalty (₦)</label>
                    <input type="number" className="form-control" value={settings.lateness_flat_penalty||0}
                      onChange={e=>setSettings({...settings,lateness_flat_penalty:e.target.value})} />
                    <div className="form-hint">Fixed amount for lateness under 1 hour</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Lateness Hourly Penalty (₦/hour)</label>
                    <input type="number" className="form-control" value={settings.lateness_hourly_penalty||0}
                      onChange={e=>setSettings({...settings,lateness_hourly_penalty:e.target.value})} />
                    <div className="form-hint">Amount per hour when lateness exceeds threshold</div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Hourly Penalty Threshold (minutes)</label>
                  <input type="number" className="form-control" value={settings.lateness_hourly_threshold||60}
                    onChange={e=>setSettings({...settings,lateness_hourly_threshold:e.target.value})} />
                  <div className="form-hint">When lateness exceeds this many minutes, switch to hourly rate (default: 60)</div>
                </div>

                <div style={{fontSize:11,fontWeight:600,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8,marginTop:16}}>Other Penalties</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Early Sign-out Penalty (₦ per day)</label>
                    <input type="number" className="form-control" value={settings.early_signout_penalty||0}
                      onChange={e=>setSettings({...settings,early_signout_penalty:e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Absence Penalty (₦ per day)</label>
                    <input type="number" className="form-control" value={settings.absence_penalty_per_day||0}
                      onChange={e=>setSettings({...settings,absence_penalty_per_day:e.target.value})} />
                    <div className="form-hint">Only for unapproved absences</div>
                  </div>
                </div>

                <div style={{fontSize:11,fontWeight:600,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8,marginTop:16}}>Break Policy</div>
                <div className="form-group">
                  <label className="form-label">Break Allowance (minutes)</label>
                  <input type="number" className="form-control" value={settings.break_allowance_minutes}
                    onChange={e=>setSettings({...settings,break_allowance_minutes:e.target.value})} />
                  <div className="form-hint">How many minutes staff are allowed for break (default: 30)</div>
                </div>

                <div style={{fontSize:11,fontWeight:600,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8,marginTop:16}}>Task Penalties</div>
                <div style={{background:'#F2EFE7',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:12,color:'#556B2F'}}>
                  Penalties are applied at payroll processing time based on task status at due date.
                  Tasks remain open after penalty — staff must still complete them.
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tier 1 — Never Started (₦ per task)</label>
                    <input type="number" className="form-control" value={settings.task_never_started_penalty||0}
                      onChange={e=>setSettings({...settings,task_never_started_penalty:e.target.value})} />
                    <div className="form-hint">Task still pending at due date — highest penalty</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tier 2 — Incomplete (₦ per task)</label>
                    <input type="number" className="form-control" value={settings.task_incomplete_penalty||0}
                      onChange={e=>setSettings({...settings,task_incomplete_penalty:e.target.value})} />
                    <div className="form-hint">Task started but not finished by due date</div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tier 3 — Late Completion (₦ per task)</label>
                    <input type="number" className="form-control" value={settings.task_late_completion_penalty||0}
                      onChange={e=>setSettings({...settings,task_late_completion_penalty:e.target.value})} />
                    <div className="form-hint">Task completed after due date but before payroll</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tier 4 — On Time</label>
                    <input type="number" className="form-control" value={0} disabled
                      style={{background:'#F9F9F9',color:'#8A8A8A'}} />
                    <div className="form-hint">No penalty — completed before due date</div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Break Penalty per Excess Minute (₦)</label>
                  <input type="number" className="form-control" value={settings.break_penalty_per_minute}
                    onChange={e=>setSettings({...settings,break_penalty_per_minute:e.target.value})} />
                  <div className="form-hint">Amount deducted per minute over the allowed break time. Set 0 to disable penalty.</div>
                </div>
                <div style={{
                  marginTop:20,
                  background:'#F2EFE7',
                  border:'1px solid #DED5C5',
                  borderRadius:10,
                  padding:16
                }}>
                  <div style={{
                    fontSize:11,
                    fontWeight:700,
                    color:'#556B2F',
                    textTransform:'uppercase',
                    letterSpacing:'.08em',
                    marginBottom:6
                  }}>
                    Extra-Time Operational Defaults
                  </div>

                  <div style={{
                    fontSize:11,
                    color:'#6F6258',
                    lineHeight:1.5,
                    marginBottom:16
                  }}>
                    Approved overtime, night shifts and
                    weekend work are calculated per verified
                    minute. Managers and other staff have
                    separate rates. Payroll uses the effective-month rates in Payroll Settings.
                  </div>

                  <div style={{
                    fontSize:12,
                    fontWeight:700,
                    color:'#3B2A21',
                    marginBottom:8
                  }}>
                    Overtime
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Staff Rate (₦ per minute)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={
                          settings
                            .staff_overtime_rate_per_minute ??
                          0
                        }
                        onChange={e =>
                          setSettings({
                            ...settings,
                            staff_overtime_rate_per_minute:
                              e.target.value
                          })
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Manager Rate (₦ per minute)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={
                          settings
                            .manager_overtime_rate_per_minute ??
                          0
                        }
                        onChange={e =>
                          setSettings({
                            ...settings,
                            manager_overtime_rate_per_minute:
                              e.target.value
                          })
                        }
                      />
                    </div>
                  </div>

                  <div style={{
                    fontSize:12,
                    fontWeight:700,
                    color:'#3B2A21',
                    marginTop:10,
                    marginBottom:8
                  }}>
                    Night Shift
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Staff Rate (₦ per minute)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={
                          settings
                            .staff_night_rate_per_minute ??
                          0
                        }
                        onChange={e =>
                          setSettings({
                            ...settings,
                            staff_night_rate_per_minute:
                              e.target.value
                          })
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Manager Rate (₦ per minute)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={
                          settings
                            .manager_night_rate_per_minute ??
                          0
                        }
                        onChange={e =>
                          setSettings({
                            ...settings,
                            manager_night_rate_per_minute:
                              e.target.value
                          })
                        }
                      />
                    </div>
                  </div>

                  <div style={{
                    fontSize:12,
                    fontWeight:700,
                    color:'#3B2A21',
                    marginTop:10,
                    marginBottom:8
                  }}>
                    Weekend Work
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Staff Rate (₦ per minute)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={
                          settings
                            .staff_weekend_rate_per_minute ??
                          0
                        }
                        onChange={e =>
                          setSettings({
                            ...settings,
                            staff_weekend_rate_per_minute:
                              e.target.value
                          })
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Manager Rate (₦ per minute)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={
                          settings
                            .manager_weekend_rate_per_minute ??
                          0
                        }
                        onChange={e =>
                          setSettings({
                            ...settings,
                            manager_weekend_rate_per_minute:
                              e.target.value
                          })
                        }
                      />
                    </div>
                  </div>

                  <div style={{
                    background:'#FFF8E7',
                    border:'1px solid #E5D39B',
                    borderRadius:8,
                    padding:'10px 12px',
                    color:'#7A5B14',
                    fontSize:11,
                    lineHeight:1.5
                  }}>
                    A zero rate disables automatic payment
                    for that employee class and earning
                    category. Approved work will not be
                    silently imported at ₦0.
                  </div>
                </div>

                <button className="btn btn-primary" onClick={saveSettings} disabled={savingSettings}>
                  {savingSettings?'Saving...':'Save Settings'}
                </button>
              </div>
            </div>
          )}

          {activeTab==='uploads' && (
            <div>
              {uploads.length === 0 ? (
                <div className="empty-state"><h3>No uploads yet</h3></div>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'#556B2F'}}>
                    {['Filename','Period','Records','Uploaded By','Date', user?.role==='super_admin'?'':''].map(h=>(
                      <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,
                        color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {uploads.map((u,i) => (
                      <tr key={u.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{u.filename}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                          {u.period_start} → {u.period_end}
                        </td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center',
                          fontWeight:600,color:'#556B2F'}}>{u.records_imported}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>{u.uploaded_by_name}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>
                          {new Date(u.created_at).toLocaleDateString('en-NG')}
                        </td>
                        {user?.role==='super_admin' && (
                          <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                            <button className="btn btn-danger btn-sm" style={{fontSize:10}}
                              onClick={()=>deleteUpload(u)}>Delete</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
      {rejectModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-hdr">
              <span className="modal-title">Reject Request</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setRejectModal(null)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Reason for rejection *</label>
                <textarea className="form-control" rows={3} value={rejectReason}
                  onChange={e=>setRejectReason(e.target.value)} placeholder="Explain why..." />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setRejectModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={rejectRequest} disabled={!rejectReason}>Reject</button>
            </div>
          </div>
        </div>
      )}

          {activeTab==='kiosk' && (
            <div>
              <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:16}}>
                <input type="date" className="form-control" value={kioskDate}
                  onChange={e=>setKioskDate(e.target.value)}
                  style={{maxWidth:180}} />
                <span style={{fontSize:12,color:'#8A8A8A'}}>{kioskSessions.length} records today</span>
              </div>
              {kioskLoading ? <div className="spinner"/> : (
              <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'#2C1F16'}}>
                      {['Staff','Role','Sign In','Sign Out','Break Out','Break In','Break Mins','Status'].map(h=>(
                        <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                          color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kioskSessions.length === 0 ? (
                      <tr><td colSpan={8} style={{padding:20,textAlign:'center',color:'#8A8A8A'}}>
                        No kiosk sessions for this date
                      </td></tr>
                    ) : kioskSessions.map((s,i)=>(
                      <tr key={s.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{s.name}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A',
                          textTransform:'capitalize'}}>{s.role?.replace(/_/g,' ')}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#2E6B3A'}}>
                          {s.sign_in_time ? new Date(s.sign_in_time).toLocaleTimeString('en-NG',{hour:'2-digit',minute:'2-digit'}) : '—'}
                        </td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>
                          {s.sign_out_time ? new Date(s.sign_out_time).toLocaleTimeString('en-NG',{hour:'2-digit',minute:'2-digit'}) : '—'}
                        </td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                          {s.break_out ? new Date(s.break_out).toLocaleTimeString('en-NG',{hour:'2-digit',minute:'2-digit'}) : '—'}
                        </td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                          {s.break_in ? new Date(s.break_in).toLocaleTimeString('en-NG',{hour:'2-digit',minute:'2-digit'}) : '—'}
                        </td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                          {s.break_duration ? s.break_duration+' mins' : '—'}
                        </td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                          {s.sign_in_time ? (
                            <span style={{fontSize:9,padding:'3px 8px',borderRadius:20,fontWeight:600,
                              background:s.sign_out_time?'#E8F5EB':'#FFF3DC',
                              color:s.sign_out_time?'#2E6B3A':'#8B5E0A'}}>
                              {s.sign_out_time?'Signed Out':'Still In'}
                            </span>
                          ) : (
                            <span style={{fontSize:9,padding:'3px 8px',borderRadius:20,fontWeight:600,
                              background:'#F5F5F5',color:'#aaa'}}>Not Signed In</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}

          {activeTab==='reconciliation' && (
            <div>
              <div style={{
                background:'#EDF3FF',
                border:'1px solid #B8D0FF',
                borderRadius:10,
                padding:12,
                marginBottom:16,
                fontSize:12,
                color:'#3B5BDB'
              }}>
                Reconciliation compares T500 biometric records with
                BloansBooks kiosk sessions. A mismatch is not automatically
                a payroll deduction. Every flagged irregularity must be
                investigated before payroll payment.
              </div>

              <div style={{
                display:'flex',
                gap:10,
                alignItems:'center',
                marginBottom:16,
                flexWrap:'wrap'
              }}>
                <input
                  type="date"
                  className="form-control"
                  value={reconFrom}
                  onChange={e=>setReconFrom(e.target.value)}
                  style={{maxWidth:160}}
                />

                <span style={{fontSize:12,color:'#8A8A8A'}}>
                  to
                </span>

                <input
                  type="date"
                  className="form-control"
                  value={reconTo}
                  onChange={e=>setReconTo(e.target.value)}
                  style={{maxWidth:160}}
                />

                <label style={{
                  display:'flex',
                  alignItems:'center',
                  gap:6,
                  fontSize:12
                }}>
                  <input
                    type="checkbox"
                    checked={reconReviewOnly}
                    onChange={e=>setReconReviewOnly(e.target.checked)}
                  />
                  Show unresolved only
                </label>

                <button
                  className="btn btn-primary btn-sm"
                  onClick={loadReconciliation}
                  disabled={reconLoading}
                >
                  {reconLoading
                    ? 'Loading...'
                    : 'Load Reconciliation'}
                </button>
              </div>

              {reconError && (
                <div className="alert alert-error mb-md">
                  {reconError}
                </div>
              )}

              {(reconciliation.length > 0 ||
                reconSummary.total > 0) && (
                <div style={{
                  display:'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit,minmax(135px,1fr))',
                  gap:10,
                  marginBottom:16
                }}>
                  {[
                    ['Records',reconSummary.total,'#3B2A21'],
                    ['Unresolved',reconSummary.requires_review,'#8F2E24'],
                    ['Matched',reconSummary.matched,'#2E6B3A'],
                    ['Pending',reconSummary.pending,'#8B5E0A'],
                    ['Investigated',reconSummary.investigated,'#3B5BDB']
                  ].map(([label,value,color])=>(
                    <div
                      key={label}
                      style={{
                        background:'#fff',
                        border:'1px solid #E8E0D4',
                        borderRadius:10,
                        padding:'12px 14px'
                      }}
                    >
                      <div style={{
                        fontSize:10,
                        color:'#777',
                        textTransform:'uppercase'
                      }}>
                        {label}
                      </div>
                      <div style={{
                        fontSize:22,
                        fontWeight:700,
                        color,
                        marginTop:3
                      }}>
                        {value || 0}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {reconciliation.some(
                row => row.upload_status === 'partial'
              ) && (
                <div style={{
                  background:'#FFF3DC',
                  border:'1px solid #E7C680',
                  borderRadius:10,
                  padding:12,
                  marginBottom:16,
                  fontSize:12,
                  color:'#8B5E0A'
                }}>
                  This includes a partial-period T500 upload.
                  Pending rows are provisional and this file cannot authorize
                  payroll payment.
                </div>
              )}

              {!reconLoading &&
                !reconError &&
                reconciliation.length === 0 && (
                  <div className="empty-state">
                    <h3>No reconciliation records found</h3>
                    <p>
                      Select a date range containing an imported T500 file.
                    </p>
                  </div>
                )}

              {reconciliation.length > 0 && (
                <div style={{
                  background:'#fff',
                  border:'1px solid #E8E0D4',
                  borderRadius:12,
                  overflowX:'auto'
                }}>
                  <table style={{
                    width:'100%',
                    minWidth:1180,
                    borderCollapse:'collapse',
                    fontSize:12
                  }}>
                    <thead>
                      <tr style={{background:'#2C1F16'}}>
                        {[
                          'Date',
                          'Staff',
                          'T500 In',
                          'T500 Out',
                          'Kiosk In',
                          'Kiosk Out',
                          'Difference',
                          'Finding',
                          'Upload',
                          'Status',
                          'Action'
                        ].map(header=>(
                          <th
                            key={header}
                            style={{
                              padding:'10px 12px',
                              textAlign:'left',
                              fontSize:9,
                              color:'#E8C466',
                              textTransform:'uppercase',
                              letterSpacing:'.06em',
                              whiteSpace:'nowrap'
                            }}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {reconciliation.map(record=>{
                        const statusColors = {
                          match: {
                            background:'#E8F5EB',
                            color:'#2E6B3A'
                          },
                          mismatch: {
                            background:'#FDEAEA',
                            color:'#8F2E24'
                          },
                          pending: {
                            background:'#FFF3DC',
                            color:'#8B5E0A'
                          },
                          investigated: {
                            background:'#EDF3FF',
                            color:'#3B5BDB'
                          }
                        };

                        const colors =
                          statusColors[record.status] ||
                          statusColors.pending;

                        return (
                          <tr
                            key={record.id}
                            style={{
                              background:record.requires_review
                                ? '#FFF8F8'
                                : '#fff'
                            }}
                          >
                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8',
                              whiteSpace:'nowrap'
                            }}>
                              {record.date}
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8',
                              fontWeight:600
                            }}>
                              {record.staff_name}
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8'
                            }}>
                              {reconciliationTime(record.t500_in)}
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8'
                            }}>
                              {reconciliationTime(record.t500_out)}
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8'
                            }}>
                              {reconciliationTime(record.kiosk_in)}
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8'
                            }}>
                              {reconciliationTime(record.kiosk_out)}
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8',
                              whiteSpace:'nowrap'
                            }}>
                              In: {
                                record.in_diff_mins === null ||
                                record.in_diff_mins === undefined
                                  ? 'N/A'
                                  : record.in_diff_mins + ' min'
                              }
                              <br />
                              Out: {
                                record.out_diff_mins === null ||
                                record.out_diff_mins === undefined
                                  ? 'N/A'
                                  : record.out_diff_mins + ' min'
                              }
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8',
                              minWidth:220
                            }}>
                              {reconciliationReason(
                                record.discrepancy_type
                              )}

                              {record.investigation_notes && (
                                <div style={{
                                  marginTop:5,
                                  color:'#3B5BDB',
                                  fontSize:11
                                }}>
                                  Decision: {
                                    record.investigation_notes
                                  }
                                  {record.investigated_by_name
                                    ? ' by ' +
                                      record.investigated_by_name
                                    : ''}
                                </div>
                              )}
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8',
                              minWidth:130
                            }}>
                              {record.upload_filename || 'Unknown'}
                              <div style={{
                                fontSize:10,
                                marginTop:3,
                                color:record.upload_status === 'partial'
                                  ? '#8B5E0A'
                                  : '#2E6B3A'
                              }}>
                                {record.upload_status || 'Unknown'}
                              </div>
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8'
                            }}>
                              <span style={{
                                display:'inline-block',
                                fontSize:9,
                                padding:'3px 8px',
                                borderRadius:20,
                                fontWeight:600,
                                background:colors.background,
                                color:colors.color,
                                textTransform:'capitalize'
                              }}>
                                {record.status}
                              </span>
                            </td>

                            <td style={{
                              padding:'10px 12px',
                              borderBottom:'1px solid #EDE7D8'
                            }}>
                              {record.requires_review &&
                                ['super_admin','admin','hr_officer']
                                  .includes(user?.role) && (
                                  <button
                                    className="btn btn-outline btn-sm"
                                    style={{
                                      fontSize:10,
                                      color:'#2E6B3A',
                                      borderColor:'#2E6B3A',
                                      whiteSpace:'nowrap'
                                    }}
                                    onClick={() =>
                                      investigateReconciliation(record)
                                    }
                                  >
                                    Investigate
                                  </button>
                                )}

                              {!record.requires_review && (
                                <span style={{
                                  fontSize:10,
                                  color:'#777'
                                }}>
                                  No action required
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

    </div>
  );
}

function ProductionSupportFields({ value, onChange }) {
  return (
    <div style={{border:'1px solid #D8CFBC',padding:12,borderRadius:8,marginBottom:12}}>
      <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13}}>
        <input type="checkbox" checked={Boolean(value.production_support)}
          onChange={event => onChange({...value, production_support:event.target.checked})} />
        This extra time is to help production.
      </label>
      <label className="form-label" style={{display:'block',marginTop:10}}>
        Production work being supported *
      </label>
      <textarea className="form-control" rows={2} minLength={10}
        value={value.production_support_note || ''}
        onChange={event => onChange({...value, production_support_note:event.target.value})}
        placeholder="Describe the production job and how the selected staff will help." />
      <p style={{fontSize:12,color:'#666',margin:'8px 0 0'}}>
        Approval authorizes the work date. Pay uses actual reconciled kiosk and T500 minutes,
        excluding breaks. Estimated hours do not determine the payment.
      </p>
    </div>
  );
}
